import {Injectable} from "@angular/core";
import {BehaviorSubject, defer, EMPTY, from, merge, Observable, Subject, throwError} from "rxjs";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {catchError, finalize, ignoreElements} from 'rxjs/operators';
import {Temporal} from "temporal-polyfill";
import Duration = Temporal.Duration;
import {Lydnivåmåler} from "felles-ng2/tjenester/Lydnivåmåler";

const debug = initDebugger('Mikrofonopptak');

/**
 * Tar opp lyd fra mikrofonen og leverer opptaket som en strøm av selvstendige lydfiler.
 *
 * Opptakeren byttes først når det har gått {@link MikrofonopptakInnstillinger.minimumFragmentlengdeMs}
 * *og* det er et 'opphold i samtale', slik at hvert fragment blir en komplett fil (med headere) som ikke kutter et ord på midten.
 * Kommer det ikke noe 'opphold i samtale' før {@link MikrofonopptakInnstillinger.maksimumFragmentlengdeMs}, byttes opptakeren
 * uansett – da midt i det som blir sagt – slik at fragmentene ikke vokser uten grense.
 * Den nye opptakeren startes før den gamle stoppes, slik at ingen lyd går tapt i byttet.
 *
 * Stillheten måles av en {@link Lydnivåmåler} (AudioWorklet), slik at oppdelingen fungerer også når fanen ligger i bakgrunnen.
 *
 * Klassen har ikke noe forhold til hva fragmentene skal brukes til – den som abonnerer på {@link start}
 * bestemmer selv om de skal sendes til en server, lagres eller forkastes.
 */
export class Mikrofonopptak {
    private readonly innstillinger: MikrofonopptakInnstillinger;

    private økt?: Opptaksøkt;
    private readonly opptaksstart = new Subject<void>();
    private readonly lydnivået = new BehaviorSubject<number>(0);
    private readonly varigheten = new BehaviorSubject<Duration>(ingenVarighet);
    private readonly mikrofonstatusen = new BehaviorSubject<Mikrofonstatus>('tilkoblet');

    private lydstrøm?: MediaStream;
    /** Fjerner lytterne på lydsporet, se {@link lyttPåLydspor} */
    private fjernLydsporlyttere?: () => void;
    private lydnivåmåler?: Lydnivåmåler;

    private aktivOpptaker?: MediaRecorder;

    private pauset = false;
    private sisteFragmentTid = 0;
    /** Opptakstid fra måleperioder som er avsluttet av {@link pause} */
    private bokførtVarighetMs = 0;
    /** Starttidspunktet for måleperioden som løper nå – uten verdi når opptaket er pauset eller ikke i gang */
    private måleperiodeStart?: number;
    /** Hele sekunder som sist ble meldt på {@link varighet}, slik at telleren ikke melder én gang per måling */
    private meldteSekunder = 0;
    private stillhetStart?: number;
    /** Tidspunktet et bestilt bytte senest må skje, se {@link nyLydfilVedFørsteStillhet} */
    private tvungetBytteFrist?: number;

    constructor(innstillinger: Partial<MikrofonopptakInnstillinger> = {}) {
        this.innstillinger = {...standardinnstillinger, ...innstillinger};
    }

    get erIGang(): boolean {
        return erIkkeNil(this.økt);
    }

    get erPauset(): boolean {
        return this.pauset;
    }

    /** Sier fra når mikrofonen faktisk er i gang, dvs etter at brukeren har gitt tilgang */
    get vedOpptaksstart(): Observable<void> {
        return this.opptaksstart.asObservable();
    }

    /** Målt lydnivå fra 0 til 1, oppdatert for hver måling (ca 20 ganger i sekundet) mens opptaket går. Ment for visning av lydstyrke */
    get lydnivå(): Observable<number> {
        return this.lydnivået.asObservable();
    }

    /**
     * Hvor lenge opptaket har vart, målt fra start. Tiden opptaket står i {@link pause} teller ikke med.
     *
     * Oppdateres omtrent én gang i sekundet mens opptaket går, og står stille mens opptaket er pauset.
     * Etter {@link stopp} beholdes den samlede opptakstiden, og telleren nullstilles først når et nytt opptak starter.
     * Ment for visning av en teller.
     */
    get varighet(): Observable<Duration> {
        return this.varigheten.asObservable();
    }

    /**
     * Tilstanden til mikrofonen mens opptaket går. Er `tilkoblet` når opptaket ikke er i gang.
     *
     * Kobles mikrofonen fra, leveres lyden som er tatt opp så langt som en lydfil, og opptaket slutter å
     * fange opp lyd – både lydnivået og {@link varighet} står stille. Opptaket må likevel {@link stopp}es,
     * og et nytt startes med en annen mikrofon.
     */
    get mikrofonstatus(): Observable<Mikrofonstatus> {
        return this.mikrofonstatusen.asObservable();
    }

    /** Varigheten akkurat nå, for de som trenger den mellom oppdateringene på {@link varighet} */
    get varighetNå(): Duration {
        return this.somVarighet(this.måltVarighetMs(Date.now()));
    }

    /**
     * Starter opptak ved abonnering, og leverer et {@link Lydfragment} hver gang en lydfil er ferdig.
     *
     * Strømmen fullføres av {@link stopp}, som først leverer det siste fragmentet. Avsluttes abonnementet
     * i stedet, avbrytes opptaket umiddelbart og lyd som ikke er levert enda går tapt.
     * Feiler mikrofontilgangen, leveres feilen på strømmen. Det samme gjelder om en ny opptaker ikke
     * lar seg starte underveis, se {@link byttOpptaker} – da leveres lyden så langt før feilen. Kun ett abonnement kan være aktivt om gangen,
     * og fragmentnummereringen starter på nytt for hvert opptak.
     */

    start(): Observable<Lydfragment> {
        // 1. defer sikrer at logikken evalueres idet noen faktisk abonnerer
        return defer(() => {
            if (this.erIGang) {
                return throwError(() => new Error('Opptak er allerede i gang'));
            }

            const økt = this.økt = new Opptaksøkt();
            this.nullstillVarighet();
            this.mikrofonstatusen.next('tilkoblet');

            // 2. Vi gjør om Promise-kallet til en Observable for å koble feilhåndteringen til strømmen
            const opptakOppstart$ = from(this.startOpptak()).pipe(
              ignoreElements(), // Vi bryr oss ikke om verdien Promiset returnerer, bare evt. feil
              catchError(feil => {
                  if (this.økt !== økt) return EMPTY; // Sene feil ignoreres

                  console.error('Feil ved tilgang til mikrofon:', feil);
                  this.frigiRessurser();
                  return throwError(() => feil); // Sender feilen videre til abonnenten
              })
            );

            // 3. Vi fletter sammen selve lydfragmentene og oppstartsovervåkningen
            return merge(økt.fragmenter, opptakOppstart$).pipe(
              // 4. finalize kjøres uansett om strømmen fullføres, feiler, eller man unsubscriber
              finalize(() => this.stopp())
            );
        });
    }

    /*start(): Observable<Lydfragment> {
        return new Observable<Lydfragment>(abonnent => {
            if (this.erIGang) {
                abonnent.error(new Error('Opptak er allerede i gang'));
                return;
            }

            const økt = this.økt = new Opptaksøkt();
            const abonnement = økt.fragmenter.subscribe(abonnent);

            this.startOpptak().catch(feil => {
                //en sen feil hører til et opptak som allerede er avsluttet eller erstattet
                if (this.økt !== økt) return;

                console.error('Feil ved tilgang til mikrofon:', feil);
                this.frigiRessurser();
                abonnent.error(feil);
            });

            return () => {
                abonnement.unsubscribe();
                this.stopp();
            };
        });
    }*/


    /** Stopper opptaket, leverer siste fragment og fullfører strømmen fra {@link start} */
    stopp(): void {
        const økt = this.økt;
        if (erNil(økt)) return;

        //frigiRessurser stopper opptakeren, som i sin tur leverer det siste fragmentet til økten
        this.frigiRessurser();
        økt.avslutt();
    }

    /**
     * Bestiller en ny lydfil ved første 'opphold i samtale', uavhengig av {@link MikrofonopptakInnstillinger.minimumFragmentlengdeMs}.
     * Kommer det ingen 'opphold i samtale' innen `maksVentetidMs`, klippes lydfilen uansett – da midt i det som blir sagt.
     *
     * Nyttig når noe utenfor opptaket trenger et snarlig fragment, feks at brukeren har bedt om en oppsummering.
     * Kalles metoden flere ganger, er det den tidligste fristen som gjelder. Bestillingen gjelder kun neste bytte.
     */
    nyLydfilVedFørsteStillhet(maksVentetidMs: number): void {
        if (!this.erIGang) {
            debug('nyLydfilVedFørsteStillhet ignorert, opptak er ikke i gang');
            return;
        }

        const frist = Date.now() + maksVentetidMs;
        this.tvungetBytteFrist = Math.min(frist, this.tvungetBytteFrist ?? frist);
        debug(`ny lydfil bestilt, frist om ${this.tvungetBytteFrist - Date.now()} ms`);
    }

    /**
     * Avslutter lydfilen som er under opptak og leverer den, og setter opptaket på pause.
     * Den nye opptakeren står i pause – og fanger dermed ingen lyd – helt til {@link fortsett} kalles.
     */
    pause(): void {
        if (!this.erIGang) {
            debug('pause ignorert, opptak er ikke i gang');
            return;
        }
        if (this.pauset) return;

        this.pauset = true;
        const nå = Date.now();
        //telleren stopper på pausetidspunktet, og måler videre først fra fortsett
        this.stoppVarighetsmåling(nå);
        //pausen leverer en ny lydfil, så en eventuell bestilling fra nyLydfilVedFørsteStillhet er innfridd
        this.tvungetBytteFrist = undefined;
        //byttet leverer lyden så langt som en ferdig fil, og den nye opptakeren settes i pause av startNyOpptaker
        if (erIkkeNil(this.aktivOpptaker)) this.byttOpptaker(nå);
    }

    /** Fortsetter et opptak som er satt på pause av {@link pause} */
    fortsett(): void {
        if (!this.pauset) {
            debug('fortsett ignorert, opptaket er ikke pauset');
            return;
        }

        this.pauset = false;
        //en frakoblet mikrofon har ingen opptaker å fortsette, og telleren skal stå stille
        if (this.erFrakoblet) return;

        this.aktivOpptaker?.resume();
        const nå = Date.now();
        //lydfilen begynner å fylles først nå, så minimumslengden måles herfra
        this.sisteFragmentTid = nå;
        this.måleperiodeStart = nå;
        this.stillhetStart = undefined;
    }

    //private hjelpemetoder

    private async startOpptak(): Promise<void> {
        //økten fanges før await-en, slik at vi kan se om opptaket ble stoppet eller erstattet mens vi ventet
        const økt = this.økt;
        const deviceId = this.innstillinger.deviceId;
        const lydstrøm = await navigator.mediaDevices.getUserMedia({audio: erIkkeNil(deviceId) ? {deviceId: {exact: deviceId}} : true});

        if (this.økt !== økt) {
            lydstrøm.getTracks().forEach(spor => spor.stop());
            return;
        }
        this.lydstrøm = lydstrøm;
        this.lyttPåLydspor(lydstrøm);

        //måleren startes før opptakeren, slik at et opptak der stillhetsmålingen ikke kommer i gang feiler uten å ha fanget lyd
        const lydnivåmåler = this.lydnivåmåler = new Lydnivåmåler(måleintervallMs);
        await lydnivåmåler.start(lydstrøm, rms => this.vedMåling(rms));
        //stopp eller et nytt opptak mens worklet-modulen ble lastet har allerede frigitt måleren og mikrofonen
        if (this.økt !== økt) return;

        this.aktivOpptaker = this.startNyOpptaker();
        const nå = Date.now();
        this.sisteFragmentTid = nå;
        //et opptak som ble pauset før mikrofontilgangen var avklart fanger ingen lyd, telleren venter da på fortsett
        if (!this.pauset) this.måleperiodeStart = nå;

        this.opptaksstart.next();
    }

    private get erFrakoblet(): boolean {
        return this.mikrofonstatusen.value === 'frakoblet';
    }

    /** Følger med på om mikrofonen kobles fra eller dempes, se {@link mikrofonstatus} */
    private lyttPåLydspor(lydstrøm: MediaStream): void {
        const spor = lydstrøm.getTracks();
        const vedFrakobling = () => this.vedFrakobling();
        const vedDemping = () => {
            if (!this.erFrakoblet) this.mikrofonstatusen.next('dempet');
        };
        const vedOpphevetDemping = () => {
            if (!this.erFrakoblet) this.mikrofonstatusen.next('tilkoblet');
        };

        spor.forEach(lydspor => {
            lydspor.addEventListener('ended', vedFrakobling);
            lydspor.addEventListener('mute', vedDemping);
            lydspor.addEventListener('unmute', vedOpphevetDemping);
        });
        this.fjernLydsporlyttere = () => spor.forEach(lydspor => {
            lydspor.removeEventListener('ended', vedFrakobling);
            lydspor.removeEventListener('mute', vedDemping);
            lydspor.removeEventListener('unmute', vedOpphevetDemping);
        });

        //sporet kan allerede være dempet når opptaket starter
        if (spor.some(lydspor => lydspor.muted)) vedDemping();
    }

    /**
     * Mikrofonen er borte, og en ny opptaker kan ikke lages av lydstrømmen. Lyden så langt leveres,
     * og målingen stoppes slik at hverken lydnivå eller varighet later som om det tas opp. Økten står
     * åpen til {@link stopp}, slik at den som abonnerer får avsluttet opptaket på vanlig måte.
     */
    private vedFrakobling(): void {
        if (erNil(this.økt) || this.erFrakoblet) return;
        debug('mikrofonen er koblet fra');

        this.mikrofonstatusen.next('frakoblet');

        this.lydnivåmåler?.stopp();
        this.lydnivåmåler = undefined;

        //opptakeren kan ha stoppet av seg selv da sporet ble avsluttet, da har den allerede levert lydfila
        this.stoppOpptaker(this.aktivOpptaker);
        this.aktivOpptaker = undefined;

        this.stillhetStart = undefined;
        this.tvungetBytteFrist = undefined;
        this.stoppVarighetsmåling(Date.now());
        this.varigheten.next(this.varighetNå);
        this.lydnivået.next(0);
    }

    /** Kalles for hver måling fra {@link Lydnivåmåler}: melder lydnivå og varighet, og bytter opptaker når kriteriene er nådd */
    private vedMåling(rms: number): void {
        if (erNil(this.økt) || erNil(this.aktivOpptaker)) return;

        const nå = Date.now();
        //1.4 er en ren visningsforsterkning, slik at normal tale gir godt utslag
        this.lydnivået.next(Math.min(1, rms * 1.4));
        this.meldVarighet(nå);

        if (this.somDesibel(rms) < this.innstillinger.stillhetTerskelDb)
            this.stillhetStart ??= nå;
        else
            this.stillhetStart = undefined;

        if (this.skalByttOpptaker(nå))
            this.byttOpptaker(nå);
    }

    private somDesibel(rms: number): number {
        return 20 * Math.log10(rms || 0.0001);
    }

    /**
     * Oppretter og starter en ny opptaker, i pause dersom opptaket er satt på pause.
     *
     * `start()` kaster feks når lydstrømmen ikke lenger er aktiv. Opptakeren har da reservert en
     * lydfil i økten som den aldri kommer til å levere, og reservasjonen må gis tilbake – ellers
     * venter økten på den for alltid, og strømmen fra {@link start} fullføres aldri.
     */
    private startNyOpptaker(): MediaRecorder {
        const økt = this.økt!;
        const opptaker = this.opprettOpptaker();
        try {
            opptaker.start();
            if (this.pauset) opptaker.pause();
        } catch (feil) {
            opptaker.ondataavailable = null;
            opptaker.onstop = null;
            if (opptaker.state !== 'inactive') opptaker.stop();
            økt.angreReservasjon();
            throw feil;
        }
        return opptaker;
    }

    private skalByttOpptaker(nå: number): boolean {
        //et pauset opptak fanger ingen lyd, og har dermed ingenting å klippe
        if (this.pauset) return false;

        const fragmentlengde = nå - this.sisteFragmentTid;
        //uten 'opphold i samtale' ville lydfila vokst uten grense, så den klippes uansett når den er lang nok
        if (fragmentlengde >= this.innstillinger.maksimumFragmentlengdeMs) return true;

        //en bestilt lydfil klippes ved første 'opphold i samtale', og senest når fristen er ute
        if (erIkkeNil(this.tvungetBytteFrist))
            return nå >= this.tvungetBytteFrist || this.erOppholdISamtale(nå);

        return fragmentlengde > this.innstillinger.minimumFragmentlengdeMs && this.erOppholdISamtale(nå);
    }

    private erOppholdISamtale(nå: number): boolean {
        return erIkkeNil(this.stillhetStart) && nå - this.stillhetStart >= this.innstillinger.krevdStillhetMs;
    }

    /** Nullstiller telleren, slik at et nytt opptak måles fra sin egen start */
    private nullstillVarighet(): void {
        this.bokførtVarighetMs = 0;
        this.måleperiodeStart = undefined;
        this.meldteSekunder = 0;
        this.varigheten.next(ingenVarighet);
    }

    /** Bokfører måleperioden som løper, slik at telleren står stille til den startes igjen */
    private stoppVarighetsmåling(nå: number): void {
        if (erNil(this.måleperiodeStart)) return;

        this.bokførtVarighetMs += nå - this.måleperiodeStart;
        this.måleperiodeStart = undefined;
    }

    private måltVarighetMs(nå: number): number {
        return this.bokførtVarighetMs + (erIkkeNil(this.måleperiodeStart) ? nå - this.måleperiodeStart : 0);
    }

    /** Melder varigheten videre først når den har passert et nytt helt sekund, slik at abonnentene slipper en melding per måling */
    private meldVarighet(nå: number): void {
        const millisekunder = this.måltVarighetMs(nå);
        const sekunder = Math.floor(millisekunder / 1000);
        if (sekunder === this.meldteSekunder) return;

        this.meldteSekunder = sekunder;
        this.varigheten.next(this.somVarighet(millisekunder));
    }

    private somVarighet(millisekunder: number): Duration {
        //balanseres opp til timer, slik at abonnentene kan lese timer, minutter og sekunder direkte
        return Duration.from({milliseconds: millisekunder}).round({largestUnit: 'hour'});
    }

    /**
     * Starter en ny opptaker umiddelbart, og stopper den gamle først etter
     * {@link MikrofonopptakInnstillinger.overlappMs} slik at ingen lyd går tapt.
     *
     * `nå` er tidsstempelet målingen tok beslutningen på, slik at bokføringen bruker samme klokkeavlesning.
     */
    private byttOpptaker(nå: number): void {
        debug('kutter opptak, starter ny lydfil');

        const forrigeOpptaker = this.aktivOpptaker;

        let nyOpptaker: MediaRecorder;
        try {
            nyOpptaker = this.startNyOpptaker();
        } catch (feil) {
            this.avbrytMedFeil(feil);
            return;
        }
        this.aktivOpptaker = nyOpptaker;

        setTimeout(() => this.stoppOpptaker(forrigeOpptaker), this.innstillinger.overlappMs);

        this.sisteFragmentTid = nå;
        this.stillhetStart = undefined;
        this.tvungetBytteFrist = undefined;
    }

    /**
     * Opptaket kan ikke fortsette, feks fordi en ny opptaker ikke lot seg starte. Byttet skjer fra
     * lydnivåmålingen eller fra {@link pause}, der ingen kan fange feilen – den leveres derfor på
     * strømmen fra {@link start}, slik at den som abonnerer får vite at opptaket er slutt.
     *
     * Opptakeren som går stoppes og leverer lyden så langt, og feilen kommer først etter den. Uten
     * det ville vi også forsøkt et nytt bytte ved hver måling, og feilet like mange ganger.
     */
    private avbrytMedFeil(feil: unknown): void {
        const økt = this.økt;
        if (erNil(økt)) return;

        console.error('Mikrofonopptaket feilet, og avbrytes:', feil);
        //frigiRessurser stopper opptakeren som går, som i sin tur leverer lydfila si til økten
        this.frigiRessurser();
        økt.avsluttMedFeil(feil);
    }

    /**
     * Lager en ny, fersk opptaker som gir en komplett, frittstående lydfil når den stoppes.
     *
     * Økten fanges i en lokal variabel fordi onstop fyrer asynkront, på et tidspunkt der `this.økt` kan
     * være nullet av {@link stopp} eller erstattet av et nytt opptak. Fragmentet skal uansett til økten
     * det hører til – uten fangsten mister vi det siste fragmentet og strømmen fullføres aldri.
     */
    private opprettOpptaker(): MediaRecorder {
        const økt = this.økt!;
        const mimeType = this.velgMimeType();
        const opptaker = new MediaRecorder(this.lydstrøm!, {mimeType});
        const fragmentId = økt.nesteFragmentId();
        const biter: Blob[] = [];
        //fragmentets varighet leses av samme teller som opptaket som helhet, så pauser holdes utenfor her også
        const varighetVedStart = this.måltVarighetMs(Date.now());

        //samle data når opptaket stoppes
        opptaker.ondataavailable = (evt: BlobEvent) => {
            if (evt.data.size > 0) biter.push(evt.data);
        };

        //når opptakeren stoppes har vi en gyldig, frittstående fil med headere
        opptaker.onstop = () => {
            opptaker.ondataavailable = null;
            opptaker.onstop = null;

            const lyddata = new Blob(biter, {type: mimeType});
            const varighetMs = this.måltVarighetMs(Date.now()) - varighetVedStart;
            //en opptaker som har stått i pause hele, eller nesten hele, sin levetid har ingen brukbar lydfil å levere
            if (lyddata.size === 0 || varighetMs < this.innstillinger.minimumLydfillengdeMs) {
                debug(`fragment ${fragmentId} er tomt eller for kort (${lyddata.size} bytes, ${varighetMs} ms) og leveres ikke`);
                økt.forkast();
                return;
            }

            const varighet = this.somVarighet(varighetMs);
            debug(`leverer fragment ${fragmentId} (${lyddata.size} bytes, ${varighet.toString()})`);
            økt.lever({id: fragmentId, lyddata, filnavn: `chunk_${fragmentId}.webm`, varighet});
        };

        return opptaker;
    }

    /** Sjekker om nettleseren støtter opus, ellers webm */
    private velgMimeType(): string {
        return MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';
    }

    /** Stopper opptakeren dersom den fortsatt går – en opptaker som allerede er stoppet har levert lydfilen sin */
    private stoppOpptaker(opptaker?: MediaRecorder): void {
        if (erIkkeNil(opptaker) && opptaker.state !== 'inactive') opptaker.stop();
    }

    /** Stopper lydnivåmålingen og opptakeren, og frigir mikrofon og lydkontekst */
    private frigiRessurser(): void {
        this.økt = undefined;
        this.pauset = false;

        this.lydnivåmåler?.stopp();
        this.lydnivåmåler = undefined;

        //opptaker.stop() fører til at onstop kalles, som leverer det siste fragmentet
        this.stoppOpptaker(this.aktivOpptaker);
        this.aktivOpptaker = undefined;

        this.fjernLydsporlyttere?.();
        this.fjernLydsporlyttere = undefined;
        this.lydstrøm?.getTracks().forEach(spor => spor.stop());
        this.lydstrøm = undefined;

        this.stillhetStart = undefined;
        this.tvungetBytteFrist = undefined;

        //telleren fryses på den samlede opptakstiden, slik at den kan leses etter at opptaket er avsluttet
        this.stoppVarighetsmåling(Date.now());
        this.varigheten.next(this.varighetNå);

        this.lydnivået.next(0);
        this.mikrofonstatusen.next('tilkoblet');
    }
}


const ingenVarighet = new Duration();

/** Hvor ofte (ms) lydnivået måles, og dermed hvor ofte kriteriene for bytte av opptaker vurderes */
const måleintervallMs = 50;

export interface MikrofonopptakInnstillinger {
    /** Mikrofonen det skal tas opp fra. Uten verdi brukes standardmikrofonen */
    deviceId?: string;
    /** Minste lengde (ms) på et lydfragment før opptakeren kan byttes ved et 'opphold i samtale' */
    minimumFragmentlengdeMs: number;
    /** Største lengde (ms) på et lydfragment – kommer det ikke noe 'opphold i samtale' innen dette, klippes lydfila uansett */
    maksimumFragmentlengdeMs: number;
    /** Lydnivå (dB) som regnes som stillhet */
    stillhetTerskelDb: number;
    /** Hvor lenge (ms) det må være stille før det regnes som en 'opphold i samtale' (ifbm skifte av aktiv oppaker) */
    krevdStillhetMs: number;
    /** Overlapp (ms) mellom gammel og ny opptaker – sikrer null tap av lyd under byttet */
    overlappMs: number;
    /**
     * Lydfiler kortere enn dette (ms) leveres ikke. Feks gir stopp rett etter {@link Mikrofonopptak.pause}
     * en lydfil med gyldige headere men tilnærmet uten lyd, som transkripsjonen ikke klarer å behandle
     */
    minimumLydfillengdeMs: number;
}

/**
 * Tilstanden til mikrofonen opptaket tas med:
 * - `tilkoblet` – mikrofonen leverer lyd
 * - `dempet` – mikrofonen er midlertidig dempet, feks av operativsystemet, og leverer ingen lyd før den oppheves
 * - `frakoblet` – mikrofonen er koblet fra eller tilgangen er trukket tilbake, og kommer ikke tilbake i dette opptaket
 */
export type Mikrofonstatus = 'tilkoblet' | 'dempet' | 'frakoblet';

/** En ferdig lydfil klippet ut av opptaket */
export interface Lydfragment {
    /** Løpenummer innenfor opptaket, det første fragmentet har nummer 1 */
    id: number;
    lyddata: Blob;
    /** Forslag til filnavn, feks ved opplasting til server */
    filnavn: string;
    /**
     * Hvor lenge denne lydfila varer, dvs tiden opptakeren fanget lyd. Tiden opptaket sto i
     * {@link Mikrofonopptak.pause} teller ikke med, på samme måte som i {@link Mikrofonopptak.varighet}.
     */
    varighet: Duration;
}

const standardinnstillinger: MikrofonopptakInnstillinger = {
    minimumFragmentlengdeMs: 60 * 1000,
    maksimumFragmentlengdeMs: 3 * 60 * 1000,
    stillhetTerskelDb: -45,
    krevdStillhetMs: 1000,
    overlappMs: 100,
    minimumLydfillengdeMs: 2000
};

/**
 * Lager {@link Mikrofonopptak}. Egen tjeneste slik at tester og storyer kan gi MøteTranskripsjon
 * et opptak som ikke bruker mikrofonen.
 */
@Injectable({providedIn: 'root'})
export class MikrofonopptakFabrikk {
    opprett(innstillinger: Partial<MikrofonopptakInnstillinger> = {}): Mikrofonopptak {
        return new Mikrofonopptak(innstillinger);
    }
}

/**
 * Tilstanden som hører til ett opptak, fra start til strømmen er fullført.
 *
 * En stoppet MediaRecorder leverer lydfilen sin asynkront, og under et opptakerbytte er to opptakere
 * i sving samtidig. Økten holder derfor styr på hvor mange lydfiler som er underveis, slik at strømmen
 * først fullføres når alle er levert.
 */
class Opptaksøkt {
    readonly fragmenter = new Subject<Lydfragment>();

    private ventendeLeveranser = 0;
    private avsluttes = false;
    private fragmentTeller = 0;
    /** Feilen strømmen skal avsluttes med, se {@link avsluttMedFeil} */
    private feil?: { årsak: unknown };
    private fullført = false;

    /** Reserverer løpenummeret til en ny opptaker, som forventes å levere en lydfil */
    nesteFragmentId(): number {
        this.ventendeLeveranser++;
        return ++this.fragmentTeller;
    }

    lever(fragment: Lydfragment): void {
        this.fragmenter.next(fragment);
        this.leveranseFerdig();
    }

    /** Melder fra om at en opptaker likevel ikke hadde noen lydfil å levere */
    forkast(): void {
        this.leveranseFerdig();
    }

    /**
     * Gir tilbake løpenummeret siste kall til {@link nesteFragmentId} reserverte, når opptakeren ikke
     * kom i gang. Løpenummeret gis til neste opptaker, så lydfilene nummereres uten hull.
     */
    angreReservasjon(): void {
        this.fragmentTeller--;
        this.leveranseFerdig();
    }

    /** Avslutter strømmen med en feil, så snart opptakerne har levert lydfilene sine */
    avsluttMedFeil(feil: unknown): void {
        this.feil = {årsak: feil};
        this.avslutt();
    }

    /** Fullfører strømmen så snart opptakerne har levert lydfilene sine */
    avslutt(): void {
        this.avsluttes = true;
        this.fullførDersomAltErLevert();
    }

    private leveranseFerdig(): void {
        this.ventendeLeveranser--;
        this.fullførDersomAltErLevert();
    }

    private fullførDersomAltErLevert(): void {
        if (!this.avsluttes || this.ventendeLeveranser > 0 || this.fullført) return;

        this.fullført = true;
        if (erIkkeNil(this.feil)) this.fragmenter.error(this.feil.årsak);
        else this.fragmenter.complete();
    }
}
